<script setup lang="ts">
import { useBillingUtils } from '@/use/billingUtils';
import { logError } from '@/lib/logError';
import { computed } from 'vue';

const props = defineProps<{
  currency: string;
  amount: number; // in smallest currency unit (e.g. cents)
  interval: 'month' | 'year' | 'lifetime' | string;
  class?: string;
}>();

const { formatPrice } = useBillingUtils();

const zeroDecimalCurrencies = new Set([
  'BIF',
  'CLP',
  'DJF',
  'GNF',
  'JPY',
  'KMF',
  'KRW',
  'MGA',
  'PYG',
  'RWF',
  'UGX',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
]);

interface FormattedPart {
  type: 'currency' | 'number' | 'literal';
  value: string;
}

const formattedParts = computed<FormattedPart[]>(() => {
  const upperCurrency = props.currency?.toUpperCase();
  if (!upperCurrency) return [];

  let total = props.amount;
  let minimumFractionDigits = 2;
  if (zeroDecimalCurrencies.has(upperCurrency)) {
    minimumFractionDigits = 0;
  } else {
    total /= 100;
    if (total % 1 === 0) minimumFractionDigits = 0;
  }

  try {
    const formatter = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: props.currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits,
    });
    return formatter.formatToParts(total).map((p): FormattedPart => {
      const value = p.value.trim();
      switch (p.type) {
        case 'currency':
          return { type: 'currency', value };
        case 'integer':
        case 'group':
        case 'decimal':
        case 'fraction':
          return { type: 'number', value };
        default:
          return { type: 'literal', value };
      }
    });
  } catch (e) {
    logError('Error formatting price:', e);
    return [{ type: 'number', value: formatPrice(props.amount, props.currency) }];
  }
});

const className = computed(() => props.class || '');
</script>

<template>
  <div :class="['billing-amount-display', className]">
    <template v-for="(part, idx) in formattedParts" :key="idx">
      <span v-if="part.type === 'currency'" class="currency">{{ part.value }}</span>
      <span v-else-if="part.type === 'number'" class="number">{{ part.value }}</span>
      <template v-else>{{ part.value }}</template>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.billing-amount-display {
  position: relative;
  font-family: var(--font-family-ui-serif, ui-serif, Georgia, serif);
  font-weight: bold;
  line-height: 1;

  span {
    display: inline-block;
  }
  .currency {
    font-size: 1.75em;
    margin-right: 0.1em;
    vertical-align: top;
  }
  .number {
    font-size: 3.5em;
  }
}
</style>
